/**
 * ApiSnippet: how to run this template from code, with the values currently in the form.
 *
 *   <ApiSnippet slug="google-maps-leads" inputs={toApiInputs(t.inputs, values)} />
 */
import { useState } from "react";
import dynamic from "next/dynamic";
import SegmentedControl from "@/components/ui/SegmentedControl";
import IconButton from "@/components/ui/IconButton";
import { copyText } from "@/lib/copy";
import { useTheme } from "@/context/ThemeContext";

const CodeHighlight = dynamic(() => import("@/components/ui/CodeHighlight"), { ssr: false });

const BASE = "https://api.spidra.io/api";
type Lang = "curl" | "javascript" | "python";

function snippets(slug: string, inputs: Record<string, unknown>): Record<Lang, string> {
  const body = JSON.stringify({ inputs }, null, 2);
  const pyInputs = JSON.stringify(inputs, null, 4)
    .replace(/\btrue\b/g, "True")
    .replace(/\bfalse\b/g, "False")
    .replace(/\bnull\b/g, "None")
    .replace(/\n/g, "\n    ");
  return {
    curl: `curl -X POST ${BASE}/templates/${slug}/runs \\
  -H "x-api-key: $SPIDRA_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${body.replace(/'/g, "'\\''")}'

# Then poll the run until "status" is no longer "running":
curl ${BASE}/template-runs/RUN_ID -H "x-api-key: $SPIDRA_API_KEY"`,
    javascript: `const headers = { "x-api-key": process.env.SPIDRA_API_KEY, "Content-Type": "application/json" };

let { run } = await fetch("${BASE}/templates/${slug}/runs", {
  method: "POST",
  headers,
  body: JSON.stringify(${body.replace(/\n/g, "\n  ")}),
}).then(r => r.json());

while (run.status === "running") {
  await new Promise(r => setTimeout(r, 3000));
  ({ run } = await fetch(\`${BASE}/template-runs/\${run.runId}\`, { headers }).then(r => r.json()));
}
console.log(run.rows);`,
    python: `import os, time, requests

headers = {"x-api-key": os.environ["SPIDRA_API_KEY"]}
run = requests.post(
    "${BASE}/templates/${slug}/runs",
    headers=headers,
    json={"inputs": ${pyInputs}},
).json()["run"]

while run["status"] == "running":
    time.sleep(3)
    run = requests.get(f"${BASE}/template-runs/{run['runId']}", headers=headers).json()["run"]

print(run["rows"])`,
  };
}

export default function ApiSnippet({ slug, inputs }: { slug: string; inputs: Record<string, unknown> }) {
  const [lang, setLang] = useState<Lang>("curl");
  const { resolvedTheme } = useTheme();
  const code = snippets(slug, inputs)[lang];
  return (
    <div className="space-y-3">
      <p className="text-13 leading-5 text-fg-secondary">
        Run this template from your code with the values in the form. Results come back as rows with the same fields as the table.
      </p>
      <div className="flex items-center justify-between gap-3">
        <SegmentedControl
          aria-label="Language"
          size="sm"
          value={lang}
          onChange={setLang}
          options={[
            { value: "curl", label: "cURL" },
            { value: "javascript", label: "Node.js" },
            { value: "python", label: "Python" },
          ]}
        />
        <IconButton icon="lucide:copy" label="Copy code" size="sm" variant="secondary" onClick={() => copyText(code, "Code")} />
      </div>
      <div className="overflow-auto rounded-control border border-line bg-surface-muted p-3">
        <CodeHighlight code={code} language={lang === "curl" ? "bash" : lang} dark={resolvedTheme === "dark"} />
      </div>
    </div>
  );
}
